import type { SiteSettings } from '@sazerac/design-tokens';
import { OpeningHoursList } from '../components/OpeningHoursList';

interface VisitUsData {
  eyebrow?: string;
}

/**
 * Address, phone and opening hours for the primary venue, straight from the database so
 * a change in the dashboard reaches the site immediately.
 */
export function VisitUsBlock({ data, site }: { data: VisitUsData; site?: SiteSettings }) {
  const location = site?.locations?.[0];
  if (!location) return null;

  const mapQuery = encodeURIComponent(
    `${location.name}, ${location.addressLine1}, ${location.postalCode ?? ''} ${location.city}, ${location.country}`,
  );

  return (
    <section className="sz-section" aria-labelledby="visit-heading">
      <div className="sz-section__inner sz-visit">
        <div>
          <p className="sz-eyebrow">{data.eyebrow ?? 'Visit Us'}</p>
          <h3 id="visit-heading" className="sz-visit__name">
            {location.name}
          </h3>
          <address className="sz-visit__address">
            {location.addressLine1}
            {location.addressLine2 && (
              <>
                <br />
                {location.addressLine2}
              </>
            )}
            <br />
            {location.postalCode} {location.city}, {location.country}
            {location.phone && (
              <>
                <br />
                <a href={`tel:${location.phone.replace(/\s/g, '')}`}>{location.phone}</a>
              </>
            )}
          </address>

          <OpeningHoursList hours={location.openingHours} />

          {location.directionsNote && <p className="sz-visit__note">{location.directionsNote}</p>}
        </div>

        {/* An embedded map needs a provider key that is not configured, so we link out to
            the visitor's own map app instead of showing an empty frame. */}
        <div className="sz-visit__map">
          {location.mapEmbedUrl ? (
            <iframe
              src={location.mapEmbedUrl}
              title={`Map showing ${location.name}`}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              style={{ width: '100%', height: '100%', border: 0 }}
            />
          ) : (
            <a
              className="sz-visit__map-link"
              href={`https://www.openstreetmap.org/search?query=${mapQuery}`}
              target="_blank"
              rel="noreferrer noopener"
            >
              <span className="sz-eyebrow" style={{ margin: 0 }}>
                Directions
              </span>
              <span className="sz-visit__map-address">
                {location.addressLine1}, {location.city}
              </span>
              <span className="sz-link-caps">Open in maps →</span>
            </a>
          )}
        </div>
      </div>
    </section>
  );
}
